import React, { useLayoutEffect, useState } from "react";

import { isEmpty, join, map } from "lodash/fp";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, IconButton, Typography } from "@mui/material";

import { WORKSPACE_INVITE_FIELD_ARRAY_NAME } from "@app/analytics/account/WorkspaceSettings/hooks/useInviteWorkspaceMembersForm";
import {
  WorkspaceInviteFormReturn,
  WorkspaceInviteInputs,
  WizardFormValues,
} from "@app/analytics/account/WorkspaceSettings/WorkspaceWizard/types";
import * as Events from "@app/analytics/tracking/events";
import useAuth from "@app/common/auth/useAuth";
import useHasMeetings from "@app/common/hooks/meetings/useHasMeetings";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import { toast } from "@app/common/hooks/useToasts";
import ROUTES from "@app/common/routes";
import { withQueryParams } from "@app/common/utils";
import useWorkspace from "@app/common/workspaces/useWorkspace";
import { yupResolver } from "@app/utils/forms/yupResolver";
import usePageTitle from "@app/utils/hooks/usePageTitle";

import Stack, { Row } from "@rui/Stack";

import CreateTeamsStep from "./CreateTeamsStep";
import CreateWorkspaceStep from "./CreateWorkspaceStep";
import InviteStep from "./InviteStep";
import ReviewSettingsStep from "./ReviewSettingsStep";
import SubscriptionPurchaseBanner from "./SubscriptionPurchaseBanner";
import useCreateWorkspace from "../hooks/useCreateWorkspace";
import useReviewSettings from "../hooks/useReviewSettings";

const wizardValidationSchema = yup.object().shape({
  workspaceName: yup
    .string()
    .required("Cannot be empty")
    .min(2, "Name must be at least 2 characters")
    .max(60, "Name cannot exceed 60 characters"),
  dataAgreement: yup
    .boolean()
    .oneOf([true], "Please agree to the Data Processing Agreement to proceed"),
});

const PAGE_TITLES = [
  "Create Workspace",
  "Create Workspace - Teams",
  "Create Workspace - Invite Members",
  "Create Workspace - Review Settings",
];

const DEFAULT_STEPS_COMPLETED = {
  1: false,
  2: false,
  3: false,
  4: false,
};

const WorkspaceWizard = () => {
  const { user } = useAuth();
  const { inWorkspace, workspaceId } = useWorkspace();
  const navigate = useNavigate();
  const { canCreateWorkspace } = useFeatureAccess();
  const { hasMeetings } = useHasMeetings({ userId: user!.id });

  const [currentStep, setCurrentStep] = useState(0);
  const [queryParams] = useSearchParams();

  const { workspaceSettings, updateWorkspaceSettings } = useReviewSettings();

  usePageTitle(PAGE_TITLES[currentStep] ?? "Create Workspace");

  // this query param will be passed from the marketing site when the user clicks on a plan
  const fromMarketingPricingPage = queryParams.get("pricing") === "true";

  // TODO: ENG-11145 refactor to use more cohesive wizard state instead or storing
  // users to invite separately
  const [usersToInvite, setUsersToInvite] = useState<WorkspaceInviteInputs[]>(
    []
  );

  // keeps track of steps completed for future use & to not fire extra mixpanel events
  const [stepsCompleted, setStepsCompleted] = useState<{
    [key: number]: boolean;
  }>(DEFAULT_STEPS_COMPLETED);

  const createWorkspace = useCreateWorkspace();
  const loading = createWorkspace.isPending;

  const form = useForm<WizardFormValues>({
    resolver: yupResolver(wizardValidationSchema),
    defaultValues: {
      teams: [],
      users: [],
      dataAgreement: false,
    },
  });

  useLayoutEffect(() => {
    if (createWorkspace.isPending) return;

    if (inWorkspace) {
      if (fromMarketingPricingPage) {
        // if the users is coming from the marketing site, land them at the billing page
        navigate(ROUTES.BILLING);
      } else {
        // users cannot create a new workspace if they are already in one!
        navigate(ROUTES.workspaceSettingsById(workspaceId!));
      }
    }
  }, [
    inWorkspace,
    fromMarketingPricingPage,
    workspaceId,
    createWorkspace.isPending,
  ]);

  const handleNextStep = async (inviteStepForm?: WorkspaceInviteFormReturn) => {
    if (!canCreateWorkspace) return;

    if (!stepsCompleted[currentStep + 1]) {
      Events.workspaceWizard({ step: currentStep + 1 });

      const newStepsCompleted = { ...stepsCompleted, [currentStep + 1]: true };

      setStepsCompleted(newStepsCompleted);
    }

    const users =
      inviteStepForm
        ?.getValues(WORKSPACE_INVITE_FIELD_ARRAY_NAME)
        .filter((user: WorkspaceInviteInputs) => user.email) ?? [];
    if (!isEmpty(users)) {
      setUsersToInvite(users);
    }

    if (currentStep === STEPS.length - 1) {
      const wizardFormValues = form.getValues();

      createWorkspace.mutate(
        {
          ...wizardFormValues,
          users: usersToInvite,
          settings: workspaceSettings,
        },
        {
          onSuccess: ({ workspace, errors }) => {
            if (errors.length) {
              const message = `We created your workspace, however some ${join(
                " and ",
                map("message", errors)
              )}. Please review your workspace.`;
              toast.warning(message);
            } else {
              toast.success(
                `Workspace created! You are now working under ${workspace.name}.`
              );
            }

            navigate(
              withQueryParams(ROUTES.WORKSPACE_SETTINGS_ROOT, {
                migrateMeetings: hasMeetings ? "true" : null,
              })
            );
          },
          onError: () => {
            toast.error(
              "Something went wrong creating your Workspace. Please try again or contact support@read.ai if the problem persists."
            );
          },
        }
      );
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    currentStep > 0
      ? setCurrentStep(currentStep - 1)
      : navigate(ROUTES.USER_SETTINGS_DEFAULT_SECTION);
  };

  const STEPS = [
    <CreateWorkspaceStep key={1} onNextStep={handleNextStep} form={form} />,
    <CreateTeamsStep key={2} onNextStep={handleNextStep} form={form} />,
    <InviteStep
      key={3}
      onNextStep={handleNextStep}
      form={form}
      loading={loading}
    />,
    <ReviewSettingsStep
      key={4}
      onNextStep={handleNextStep}
      form={form}
      loading={loading}
      workspaceSettings={workspaceSettings}
      updateWorkspaceSettings={updateWorkspaceSettings}
    />,
  ];

  return (
    <Stack sx={{ position: "relative", flexGrow: 1, bgcolor: "#fff" }}>
      <SubscriptionPurchaseBanner />
      <Stack
        sx={{ p: 3, pt: "68px" }}
        justifyContent="flex-start"
        alignItems="center"
      >
        <Stack
          sx={{ width: "100%", maxWidth: "750px", padding: "40px" }}
          spacing={3}
        >
          <Row variant="left" spacing={2}>
            <IconButton
              sx={{ width: "24px", height: "24px", borderRadius: "8px" }}
              onClick={handleBack}
            >
              <ArrowBackIcon sx={{ width: "20px", height: "20px" }} />
            </IconButton>
            <Box
              sx={{
                width: "2px",
                height: "20px",
                backgroundColor: "shades.neutral.100",
                borderRadius: "10px",
              }}
            />
            <Typography variant="labelReg" sx={{ color: "shades.neutral.500" }}>
              Step {currentStep + 1} of {STEPS.length}
            </Typography>
          </Row>
          {STEPS[currentStep]}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default WorkspaceWizard;
